/**
 * PartRateDbAnalytics.js
 *
 * Analytics dashboard for the Part Rate DB (lt_part_rate_db, ~370k rows). Because
 * the grid is server-paginated, the numbers come from a dedicated backend
 * aggregate endpoint (GET /lt-part-rate-db/analytics) rather than the loaded page.
 * A slicer bar at the top (material / type / sch / rating / …) scopes EVERY visual;
 * it is seeded from the grid's current search + filters when the modal opens.
 * Results are cached server-side; ↻ Refresh forces a recompute.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip,
  CartesianGrid, ResponsiveContainer, Legend,
} from 'recharts';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { applyLetterhead } from '../../../utils/pdfLetterhead';
import { exportFilename } from '../../../utils/nomenclature';

// Export libraries are heavy — load them lazily only when the user exports.
let _ExcelJS = null;
async function loadExcelLib() {
  if (!_ExcelJS) { _ExcelJS = (await import('exceljs')).default; }
  return _ExcelJS;
}
let _jsPDF = null;
let _autoTable = null;
async function loadPdfLibs() {
  if (!_jsPDF) {
    const [jsPDFMod, autoTableMod] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
    _jsPDF = jsPDFMod.default;
    _autoTable = autoTableMod.default;
  }
  return { jsPDF: _jsPDF, autoTable: _autoTable };
}
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const COLORS = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#64748b'];

const fmtInt = (n) => Number(n || 0).toLocaleString();
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNum = (n, d = 2) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: d });
const pct = (n, d) => (d ? `${((Number(n || 0) / Number(d)) * 100).toFixed(1)}%` : '0%');

// Slicers applied to every visual (mirror the backend FILTER_COLUMNS whitelist).
const SLICERS = [
  { key: 'material', label: 'Material' },
  { key: 'description', label: 'Type' },
  { key: 'sch', label: 'Schedule' },
  { key: 'rating', label: 'Rating' },
  { key: 'end_type', label: 'End Type' },
  { key: 'seam_type', label: 'Seam Type' },
  { key: 'join_type', label: 'Joint Type' },
  { key: 'mcaa_location', label: 'MCAA Loc' },
  { key: 'size', label: 'Size' },
];

// Size-curve options: metric (Y axis) × aggregation × legend grouping.
const SIZE_METRICS = [
  { key: 'ott', label: 'OTTAV hours', fmt: (v) => `${fmtNum(v, 3)} hr` },
  { key: 'mcaa', label: 'MCAA rate', fmt: (v) => `${fmtNum(v, 3)} hr` },
  { key: 'cost', label: 'Cost per unit (CAD)', fmt: (v) => fmtMoney(v) },
];
const SIZE_AGGS = [
  { key: 'avg', label: 'Average' },
  { key: 'sum', label: 'Sum' },
  { key: 'cnt', label: 'Count of rows' },
];

const Card = ({ label, value, sub, accent = '#6366f1' }) => (
  <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: '0.75rem 0.9rem', minWidth: 150, flex: '1 1 150px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
    <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</div>
    <div style={{ fontSize: '1.35rem', fontWeight: 700, color: accent, lineHeight: 1.15, marginTop: 2 }}>{value}</div>
    {sub && <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
  </div>
);

const Panel = ({ title, hint, children }) => (
  <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: '0.85rem 0.95rem', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
      <h4 style={{ margin: 0, fontSize: '0.85rem', color: '#334155' }}>{title}</h4>
      {hint && <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>{hint}</span>}
    </div>
    {children}
  </div>
);

const HEADER_BTN = { background: 'rgba(255,255,255,0.18)', border: 'none', color: 'white', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 };

const PartRateDbAnalytics = ({ open, onClose, instance, accounts, filters = {}, search = '' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Global slicers (seeded from the grid's filters/search each time the modal opens).
  const [slicers, setSlicers] = useState({});
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [options, setOptions] = useState({}); // col -> distinct values
  const seededRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText), 600);
    return () => clearTimeout(t);
  }, [searchText]);

  // Size-curve controls
  const [sizeMetric, setSizeMetric] = useState('ott');
  const [sizeAgg, setSizeAgg] = useState('avg');
  const [sizeGroupBy, setSizeGroupBy] = useState('material');

  // Export state
  const [exporting, setExporting] = useState(null); // 'excel' | 'pdf' | null
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddresses, setEmailAddresses] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  useEffect(() => {
    if (open && !seededRef.current) {
      seededRef.current = true;
      const seed = {};
      SLICERS.forEach(({ key }) => { if (filters[key]) seed[key] = String(filters[key]); });
      setSlicers(seed);
      setSearchText(search || '');
      setDebouncedSearch(search || '');
    }
    if (!open) seededRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Distinct values for the slicer dropdowns (fetched once per modal open).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const next = {};
      await Promise.all(SLICERS.map(async ({ key }) => {
        try {
          const resp = await authenticatedApiCall(instance, accounts, 'GET', `/lt-part-rate-db/distinct/${key}`);
          if (resp.data?.success) next[key] = resp.data.data || [];
        } catch { /* dropdown just stays empty */ }
      }));
      if (!cancelled) setOptions(next);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchAnalytics = useCallback(async (forceRefresh = false) => {
    setLoading(true); setError(null);
    try {
      const qp = new URLSearchParams();
      if (debouncedSearch.trim()) qp.set('search', debouncedSearch.trim());
      Object.entries(slicers).forEach(([k, v]) => { if (v) qp.set(k, v); });
      if (forceRefresh) qp.set('refresh', '1');
      const resp = await authenticatedApiCall(instance, accounts, 'GET', `/lt-part-rate-db/analytics?${qp.toString()}`);
      if (resp.data?.success) setData(resp.data);
      else setError(resp.data?.message || 'Failed to load analytics');
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, accounts, debouncedSearch, JSON.stringify(slicers)]);

  useEffect(() => { if (open) fetchAnalytics(); }, [open, fetchAnalytics]);

  const activeSlicerCount = Object.values(slicers).filter(Boolean).length + (searchText.trim() ? 1 : 0);

  // Pivot the size series ({size, grp, metrics}) into one row per size with a
  // column per group so recharts can render grouped bars: X = size (ordered by
  // the diameter parsed from the size text), one bar per material/sch.
  const sizeCurve = useMemo(() => {
    const rows = (sizeGroupBy === 'sch' ? data?.bySizeSch : data?.bySizeMaterial) || [];
    const dataKey = sizeAgg === 'cnt' ? 'cnt' : `${sizeAgg}_${sizeMetric}`;
    const groups = [...new Set(rows.map((r) => r.grp))];
    const bySize = new Map();
    rows.forEach((r) => {
      if (!bySize.has(r.size)) bySize.set(r.size, { size: r.size, dia: r.size_ord === null ? Infinity : Number(r.size_ord) });
      const entry = bySize.get(r.size);
      const v = Number(r[dataKey]);
      if (v > 0) entry[r.grp] = v;
    });
    const chartRows = [...bySize.values()]
      .filter((row) => groups.some((g) => row[g] !== undefined))
      .sort((a, b) => a.dia - b.dia);
    return { rows: chartRows, groups, dataKey };
  }, [data, sizeMetric, sizeAgg, sizeGroupBy]);

  const sizeFmt = sizeAgg === 'cnt' ? (v) => fmtInt(v) : (SIZE_METRICS.find((m) => m.key === sizeMetric) || SIZE_METRICS[0]).fmt;

  // MCAA vs OTTAV comparison: ONLY rows rated in both systems (apples-to-apples).
  const mcaaRows = useMemo(
    () => (data?.byMaterial || []).filter((d) => Number(d.rated_both) > 0),
    [data]
  );

  // ----- Export (xlsx / pdf / email), mirroring the MTO compare modal -----
  const dateStamp = () => new Date().toISOString().slice(0, 10);
  const scopeLabel = () => {
    const parts = [];
    if (searchText.trim()) parts.push(`search "${searchText.trim()}"`);
    SLICERS.forEach(({ key, label }) => { if (slicers[key]) parts.push(`${label}: ${slicers[key]}`); });
    return parts.length ? parts.join(' · ') : 'Whole table (no filters)';
  };

  const buildWorkbook = useCallback(async () => {
    const ExcelJS = await loadExcelLib();
    const wb = new ExcelJS.Workbook();
    wb.creator = 'LGL Tools';
    wb.created = new Date();
    const styleHeader = (row) => { row.font = { bold: true }; row.eachCell((c) => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEF2FF' } }; }); };
    const s = data?.summary || {};

    const s1 = wb.addWorksheet('Summary');
    s1.addRow(['Part Rate DB Analytics', new Date().toLocaleString()]);
    s1.addRow(['Scope', scopeLabel()]);
    s1.addRow([]);
    styleHeader(s1.addRow(['KPI', 'Value']));
    [
      ['Total rows', Number(s.total_rows || 0)], ['Distinct parts', Number(s.distinct_parts || 0)],
      ['Priced', Number(s.priced || 0)], ['No cost', Number(s.unpriced || 0)],
      ['With OTT hours', Number(s.with_ott || 0)], ['With MCAA rate', Number(s.with_mcaa || 0)],
      ['Rated in both', Number(s.with_both_rates || 0)], ['Avg MCAA/OTTAV ratio', Number(s.mcaa_ott_ratio_avg || 0)],
      ['Avg cost (priced)', Number(s.cost_avg || 0)], ['Max cost', Number(s.cost_max || 0)],
      ['Flagged', Number(s.flagged || 0)], ['With datasheet', Number(s.with_datasheet || 0)],
    ].forEach((r) => s1.addRow(r));
    s1.columns.forEach((c) => { c.width = 26; });

    const s2 = wb.addWorksheet('MCAA vs OTTAV');
    styleHeader(s2.addRow(['Material', 'OTTAV avg (both-rated)', 'MCAA avg (both-rated)', 'Ratio', 'Rated both', 'Rows']));
    mcaaRows.forEach((d) => {
      const ratio = Number(d.avg_ott_both) > 0 ? Number(d.avg_mcaa_both) / Number(d.avg_ott_both) : 0;
      s2.addRow([d.k, Number(d.avg_ott_both), Number(d.avg_mcaa_both), Number(ratio.toFixed(3)), Number(d.rated_both), Number(d.cnt)]);
    });
    s2.columns.forEach((c) => { c.width = 20; });

    const s3 = wb.addWorksheet('Size curves');
    const aggLabel = (SIZE_AGGS.find((a) => a.key === sizeAgg) || {}).label;
    const metricLabel = sizeAgg === 'cnt' ? 'Row count' : (SIZE_METRICS.find((m) => m.key === sizeMetric) || {}).label;
    s3.addRow([`${aggLabel} — ${metricLabel} by size, grouped by ${sizeGroupBy === 'sch' ? 'Schedule' : 'Material'}`]);
    styleHeader(s3.addRow(['Size', ...sizeCurve.groups]));
    sizeCurve.rows.forEach((r) => s3.addRow([r.size, ...sizeCurve.groups.map((g) => (r[g] !== undefined ? Number(r[g]) : null))]));
    s3.columns.forEach((c) => { c.width = 16; });

    const s4 = wb.addWorksheet('By material');
    styleHeader(s4.addRow(['Material', 'Rows', 'Priced', 'Avg cost', 'Avg OTT hr', 'Avg MCAA']));
    (data?.byMaterial || []).forEach((d) => s4.addRow([d.k, Number(d.cnt), Number(d.priced), Number(d.avg_cost), Number(d.avg_ott), Number(d.avg_mcaa)]));
    s4.columns.forEach((c) => { c.width = 16; });

    const s5 = wb.addWorksheet('Top cost');
    styleHeader(s5.addRow(['Part #', 'Material', 'Description', 'Size', 'Sch', 'Rating', 'Cost CAD']));
    (data?.topCost || []).forEach((r) => s5.addRow([r.part_number, r.material, r.description, r.size, r.sch, r.rating, Number(r.cost_cad)]));
    s5.columns.forEach((c) => { c.width = 16; });

    return wb;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, mcaaRows, sizeCurve, sizeAgg, sizeMetric, sizeGroupBy, slicers, searchText]);

  const buildPdfDoc = useCallback(async () => {
    const { jsPDF, autoTable } = await loadPdfLibs();
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();
    const s = data?.summary || {};
    const { y: letterheadY } = await applyLetterhead(doc, { instance, accounts, omitJobLine: true, margin: 14 });
    doc.setFontSize(14);
    doc.setTextColor(30, 41, 59);
    doc.text('Part Rate DB Analytics', 14, letterheadY);
    doc.setTextColor(90, 90, 90); doc.setFontSize(9);
    doc.text(`Generated ${new Date().toLocaleString()} — ${scopeLabel()}`, 14, letterheadY + 6);
    const headStyles = { fillColor: [79, 70, 229], textColor: 255 };
    const baseOpts = { headStyles, styles: { fontSize: 8, cellPadding: 1.5 }, margin: { left: 14, right: 14 } };

    autoTable(doc, {
      ...baseOpts, startY: letterheadY + 10,
      head: [['Total rows', 'Distinct parts', 'Priced', 'No cost', 'With OTT', 'With MCAA', 'Rated both', 'Avg cost', 'Flagged']],
      body: [[fmtInt(s.total_rows), fmtInt(s.distinct_parts), fmtInt(s.priced), fmtInt(s.unpriced), fmtInt(s.with_ott), fmtInt(s.with_mcaa), fmtInt(s.with_both_rates), fmtMoney(s.cost_avg), fmtInt(s.flagged)]],
    });
    let y = doc.lastAutoTable.finalY + 8;

    doc.setFontSize(11); doc.setTextColor(51, 65, 85);
    doc.text('MCAA vs OTTAV (rows rated in both)', 14, y);
    autoTable(doc, {
      ...baseOpts, startY: y + 3,
      head: [['Material', 'OTTAV avg', 'MCAA avg', 'Ratio', 'Rated both']],
      body: mcaaRows.map((d) => {
        const ratio = Number(d.avg_ott_both) > 0 ? Number(d.avg_mcaa_both) / Number(d.avg_ott_both) : 0;
        return [d.k, fmtNum(d.avg_ott_both, 3), fmtNum(d.avg_mcaa_both, 3), `${fmtNum(ratio, 2)}x`, fmtInt(d.rated_both)];
      }),
    });
    y = doc.lastAutoTable.finalY + 8;

    if (sizeCurve.rows.length) {
      if (y > 160) { doc.addPage(); y = 20; }
      const aggLabel = (SIZE_AGGS.find((a) => a.key === sizeAgg) || {}).label;
      const metricLabel = sizeAgg === 'cnt' ? 'Row count' : (SIZE_METRICS.find((m) => m.key === sizeMetric) || {}).label;
      doc.setFontSize(11); doc.setTextColor(51, 65, 85);
      doc.text(`Size curves — ${aggLabel} ${metricLabel} by ${sizeGroupBy === 'sch' ? 'Schedule' : 'Material'}`, 14, y);
      autoTable(doc, {
        ...baseOpts, startY: y + 3,
        head: [['Size', ...sizeCurve.groups]],
        body: sizeCurve.rows.map((r) => [r.size, ...sizeCurve.groups.map((g) => (r[g] !== undefined ? fmtNum(r[g], 3) : '—'))]),
      });
      y = doc.lastAutoTable.finalY + 8;
    }

    if ((data?.topCost || []).length) {
      if (y > 160) { doc.addPage(); y = 20; }
      doc.setFontSize(11); doc.setTextColor(51, 65, 85);
      doc.text('Most expensive parts', 14, y);
      autoTable(doc, {
        ...baseOpts, startY: y + 3,
        head: [['Part #', 'Material', 'Description', 'Size', 'Sch', 'Rating', 'Cost CAD']],
        body: data.topCost.map((r) => [r.part_number, r.material || '—', r.description || '—', r.size || '—', r.sch || '—', r.rating || '—', fmtMoney(r.cost_cad)]),
      });
    }
    return doc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, mcaaRows, sizeCurve, sizeAgg, sizeMetric, sizeGroupBy, slicers, searchText]);

  const handleDownloadExcel = async () => {
    try {
      setExporting('excel');
      const wb = await buildWorkbook();
      const buf = await wb.xlsx.writeBuffer();
      downloadBlob(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), exportFilename({ job: 'portal', reportSlug: 'PartRateDB_Analytics', ext: 'xlsx' }));
    } catch (e) { window.alert('Excel export failed: ' + (e?.message || e)); }
    finally { setExporting(null); }
  };

  const handleDownloadPdf = async () => {
    try {
      setExporting('pdf');
      const doc = await buildPdfDoc();
      doc.save(exportFilename({ job: 'portal', reportSlug: 'PartRateDB_Analytics', ext: 'pdf' }));
    } catch (e) { window.alert('PDF export failed: ' + (e?.message || e)); }
    finally { setExporting(null); }
  };

  const handleSendEmail = async () => {
    const recipients = emailAddresses.split(/[,;\s]+/).filter((e) => e.includes('@'));
    if (recipients.length === 0) { setEmailResult({ type: 'error', message: 'Enter at least one valid email address.' }); return; }
    setEmailSending(true); setEmailResult(null);
    try {
      const doc = await buildPdfDoc();
      const pdfBase64 = doc.output('datauristring').split(',')[1];
      const filename = exportFilename({ job: 'portal', reportSlug: 'PartRateDB_Analytics', ext: 'pdf' });
      const resp = await authenticatedApiCall(instance, accounts, 'POST', '/email/send', {
        to: recipients,
        subject: 'Part Rate DB Analytics report',
        htmlBody: `<p>Please find attached the Part Rate DB analytics report.</p><p>Scope: ${scopeLabel()}</p><p>Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.</p>`,
        attachments: [{ filename, fileblob: pdfBase64 }],
        feature: 'analytics_report',
      });
      if (resp.data?.success) { setEmailResult({ type: 'success', message: `Sent to ${recipients.length} recipient(s).` }); setEmailAddresses(''); }
      else setEmailResult({ type: 'error', message: resp.data?.message || 'Failed to send email.' });
    } catch (e) { setEmailResult({ type: 'error', message: e?.response?.data?.message || e?.message || 'Failed to send email.' }); }
    finally { setEmailSending(false); }
  };

  if (!open) return null;

  const s = data?.summary || {};
  const coverage = [
    { name: 'Priced', value: Number(s.priced || 0) },
    { name: 'No cost', value: Number(s.unpriced || 0) },
  ];
  const selectStyle = { fontSize: '0.74rem', padding: '3px 5px', border: '1px solid #e2e8f0', borderRadius: 6, color: '#334155', background: 'white', maxWidth: 150 };
  const materialChartHeight = Math.max(220, ((data?.byMaterial || []).length) * 26);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#f8fafc', borderRadius: 12, width: '100%', maxWidth: 1150, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)', color: 'white', padding: '0.85rem 1.15rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '12px 12px 0 0', gap: 8, flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem' }}>📊 Part Rate DB Analytics</h3>
            <div style={{ fontSize: '0.72rem', opacity: 0.85, marginTop: 2 }}>
              {activeSlicerCount ? `Scoped by ${activeSlicerCount} filter(s)` : 'Whole table (no filters applied)'}
              {data?.cached ? ' · cached' : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {data && !loading && (
              <>
                <button onClick={() => { setEmailResult(null); setShowEmailModal(true); }} title="Email report as PDF" style={HEADER_BTN}>📧 Email</button>
                <button onClick={handleDownloadPdf} disabled={exporting === 'pdf'} title="Download report as PDF" style={{ ...HEADER_BTN, cursor: exporting === 'pdf' ? 'wait' : 'pointer' }}>{exporting === 'pdf' ? '…' : '📥 PDF'}</button>
                <button onClick={handleDownloadExcel} disabled={exporting === 'excel'} title="Download report as Excel" style={{ ...HEADER_BTN, cursor: exporting === 'excel' ? 'wait' : 'pointer' }}>{exporting === 'excel' ? '…' : '📊 Excel'}</button>
              </>
            )}
            <button onClick={() => fetchAnalytics(true)} title="Recompute (bypass cache)" style={HEADER_BTN}>↻ Refresh</button>
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>
        </div>

        {/* Slicer bar — applies to ALL visuals below */}
        <div style={{ padding: '0.6rem 1.15rem', background: 'white', borderBottom: '1px solid #e5e7eb', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Filters</span>
          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search…"
            style={{ ...selectStyle, width: 120 }}
          />
          {SLICERS.map(({ key, label }) => (
            <select key={key} value={slicers[key] || ''} onChange={(e) => setSlicers((prev) => ({ ...prev, [key]: e.target.value }))} style={selectStyle} title={label}>
              <option value="">{label}: all</option>
              {(options[key] || []).map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          ))}
          {activeSlicerCount > 0 && (
            <button onClick={() => { setSlicers({}); setSearchText(''); }}
              style={{ fontSize: '0.72rem', padding: '3px 8px', border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
              ✕ Clear ({activeSlicerCount})
            </button>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: '1rem 1.15rem', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>Loading analytics…</div>
          ) : error ? (
            <div style={{ padding: '1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c' }}>{error}</div>
          ) : (
            <>
              {/* KPI cards */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                <Card label="Total rows" value={fmtInt(s.total_rows)} sub={`${fmtInt(s.distinct_parts)} distinct part #`} />
                <Card label="Priced" value={fmtInt(s.priced)} sub={`${pct(s.priced, s.total_rows)} have a cost`} accent="#10b981" />
                <Card label="No cost yet" value={fmtInt(s.unpriced)} sub={`${pct(s.unpriced, s.total_rows)} unpriced`} accent="#ef4444" />
                <Card label="With OTT hours" value={fmtInt(s.with_ott)} sub={`avg ${fmtNum(s.ott_avg, 3)} hr`} accent="#0ea5e9" />
                <Card label="MCAA vs OTTAV" value={`${fmtNum(s.mcaa_ott_ratio_avg, 2)}×`} sub={`avg MCAA ${fmtNum(s.mcaa_avg, 3)} · ${fmtInt(s.with_both_rates)} rated both`} accent="#6366f1" />
                <Card label="Avg cost (priced)" value={fmtMoney(s.cost_avg)} sub={`max ${fmtMoney(s.cost_max)}`} accent="#f59e0b" />
                <Card label="Materials / Types" value={`${fmtInt(s.distinct_materials)} / ${fmtInt(s.distinct_types)}`} sub={`${fmtInt(s.distinct_sizes)} sizes`} accent="#8b5cf6" />
                <Card label="Flagged" value={fmtInt(s.flagged)} sub={`${fmtInt(s.with_datasheet)} with datasheet`} accent="#ec4899" />
              </div>

              {/* Size curves: X = size (all sizes, scrollable), Y = agg(metric), legend = material or sch */}
              <div style={{ marginBottom: 12 }}>
                <Panel
                  title="Size curves"
                  hint={
                    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <select value={sizeAgg} onChange={(e) => setSizeAgg(e.target.value)} style={selectStyle}>
                        {SIZE_AGGS.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
                      </select>
                      {sizeAgg !== 'cnt' && (
                        <select value={sizeMetric} onChange={(e) => setSizeMetric(e.target.value)} style={selectStyle}>
                          {SIZE_METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                        </select>
                      )}
                      <span style={{ color: '#94a3b8' }}>by</span>
                      <select value={sizeGroupBy} onChange={(e) => setSizeGroupBy(e.target.value)} style={selectStyle}>
                        <option value="material">Material</option>
                        <option value="sch">Schedule</option>
                      </select>
                      <span style={{ color: '#94a3b8' }}>{sizeCurve.rows.length} size(s) — scroll →</span>
                    </span>
                  }
                >
                  {sizeCurve.rows.length === 0 ? (
                    <div style={{ padding: '1.5rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem' }}>
                      No rows with this metric in the current selection.
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
                      <div style={{ width: Math.max(1020, sizeCurve.rows.length * Math.max(34, sizeCurve.groups.length * 12)), height: 300 }}>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={sizeCurve.rows} margin={{ left: 0, right: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="size" tick={{ fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={58} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(v) => sizeFmt(v)} />
                            <Legend wrapperStyle={{ fontSize: '0.72rem' }} />
                            {sizeCurve.groups.map((g, i) => (
                              <Bar key={g} dataKey={g} name={g} fill={COLORS[i % COLORS.length]} radius={[3, 3, 0, 0]} />
                            ))}
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}
                </Panel>
              </div>

              {/* MCAA vs OTTAV comparison — ONLY rows rated in both systems */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <Panel title="MCAA vs OTTAV — avg hours by material" hint="only rows rated in BOTH systems">
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={mcaaRows} margin={{ left: 0, right: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="k" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={55} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => `${fmtNum(v, 3)} hr`} />
                      <Legend wrapperStyle={{ fontSize: '0.72rem' }} />
                      <Bar dataKey="avg_ott_both" name="OTTAV avg hr" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="avg_mcaa_both" name="MCAA avg rate" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>
                <Panel title="MCAA / OTTAV pivot" hint="ratio = MCAA ÷ OTTAV (both-rated rows)">
                  <div style={{ overflowY: 'auto', maxHeight: 240 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb', color: '#64748b', textAlign: 'left', position: 'sticky', top: 0 }}>
                          <th style={{ padding: '6px 8px' }}>Material</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>OTTAV avg</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>MCAA avg</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Ratio</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Rated both</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mcaaRows.map((d, i) => {
                          const ratio = Number(d.avg_ott_both) > 0 ? Number(d.avg_mcaa_both) / Number(d.avg_ott_both) : 0;
                          return (
                            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '5px 8px' }}>{d.k}</td>
                              <td style={{ padding: '5px 8px', textAlign: 'right' }}>{fmtNum(d.avg_ott_both, 3)}</td>
                              <td style={{ padding: '5px 8px', textAlign: 'right' }}>{fmtNum(d.avg_mcaa_both, 3)}</td>
                              <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700, color: ratio > 1 ? '#b45309' : '#047857' }}>{fmtNum(ratio, 2)}×</td>
                              <td style={{ padding: '5px 8px', textAlign: 'right', color: '#94a3b8' }}>{fmtInt(d.rated_both)}</td>
                            </tr>
                          );
                        })}
                        {mcaaRows.length === 0 && (
                          <tr><td colSpan={5} style={{ padding: '1rem', textAlign: 'center', color: '#94a3b8' }}>No rows with both an OTTAV hour and an MCAA rate in this selection.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Panel>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {/* Cost coverage */}
                <Panel title="Cost coverage" hint="priced vs no cost">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={coverage} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.name}: ${pct(e.value, s.total_rows)}`}>
                        <Cell fill="#10b981" />
                        <Cell fill="#e5e7eb" />
                      </Pie>
                      <Tooltip formatter={(v) => fmtInt(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                </Panel>

                {/* Parts by material — every material labelled, scrolls when long */}
                <Panel title="Parts by material" hint={`top ${data.topN}`}>
                  <div style={{ overflowY: 'auto', maxHeight: 300 }}>
                    <ResponsiveContainer width="100%" height={materialChartHeight}>
                      <BarChart data={data.byMaterial} layout="vertical" margin={{ left: 8, right: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="k" width={95} tick={{ fontSize: 11 }} interval={0} />
                        <Tooltip formatter={(v) => fmtInt(v)} />
                        <Bar dataKey="cnt" name="Rows" fill="#6366f1" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Panel>

                {/* Avg cost by material */}
                <Panel title="Avg cost by material" hint="priced rows only">
                  <div style={{ overflowY: 'auto', maxHeight: 300 }}>
                    <ResponsiveContainer width="100%" height={Math.max(220, (data.byMaterial.filter((d) => Number(d.avg_cost) > 0).length) * 26)}>
                      <BarChart data={data.byMaterial.filter((d) => Number(d.avg_cost) > 0)} layout="vertical" margin={{ left: 8, right: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="k" width={95} tick={{ fontSize: 11 }} interval={0} />
                        <Tooltip formatter={(v) => fmtMoney(v)} />
                        <Bar dataKey="avg_cost" name="Avg cost" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Panel>

                {/* Avg OTT hours by type */}
                <Panel title="Avg OTT hours by type" hint={`top ${data.topN}`}>
                  <div style={{ overflowY: 'auto', maxHeight: 300 }}>
                    <ResponsiveContainer width="100%" height={Math.max(220, (data.byType.filter((d) => Number(d.avg_ott) > 0).length) * 26)}>
                      <BarChart data={data.byType.filter((d) => Number(d.avg_ott) > 0)} layout="vertical" margin={{ left: 8, right: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="k" width={110} tick={{ fontSize: 10 }} interval={0} />
                        <Tooltip formatter={(v) => `${fmtNum(v, 3)} hr`} />
                        <Bar dataKey="avg_ott" name="Avg OTT hr" fill="#0ea5e9" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Panel>

                {/* By end type */}
                <Panel title="By end type" hint="row count">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data.byEndType} margin={{ left: 0, right: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="k" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={50} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => fmtInt(v)} />
                      <Bar dataKey="cnt" name="Rows" fill="#10b981" radius={[4, 4, 0, 0]}>
                        {data.byEndType.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>

                {/* By rating */}
                <Panel title="By rating / class" hint="row count">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data.byRating} margin={{ left: 0, right: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="k" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={50} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => fmtInt(v)} />
                      <Bar dataKey="cnt" name="Rows" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>

                {/* By schedule */}
                <Panel title="By schedule" hint="row count">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data.bySch} margin={{ left: 0, right: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="k" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={50} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => fmtInt(v)} />
                      <Bar dataKey="cnt" name="Rows" fill="#ec4899" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>

                {/* By joint type */}
                <Panel title="By joint type" hint="row count">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data.byJoinType} margin={{ left: 0, right: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="k" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={50} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => fmtInt(v)} />
                      <Bar dataKey="cnt" name="Rows" fill="#14b8a6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>
              </div>

              {/* Top cost table */}
              <div style={{ marginTop: 12 }}>
                <Panel title={`Most expensive parts (top ${data.topN})`} hint="by Cost CAD">
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb', color: '#64748b', textAlign: 'left' }}>
                          <th style={{ padding: '6px 8px' }}>Part #</th>
                          <th style={{ padding: '6px 8px' }}>Material</th>
                          <th style={{ padding: '6px 8px' }}>Description</th>
                          <th style={{ padding: '6px 8px' }}>Size</th>
                          <th style={{ padding: '6px 8px' }}>Sch</th>
                          <th style={{ padding: '6px 8px' }}>Rating</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Cost CAD</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.topCost || []).map((r, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>{r.part_number}</td>
                            <td style={{ padding: '5px 8px' }}>{r.material || '—'}</td>
                            <td style={{ padding: '5px 8px' }}>{r.description || '—'}</td>
                            <td style={{ padding: '5px 8px' }}>{r.size || '—'}</td>
                            <td style={{ padding: '5px 8px' }}>{r.sch || '—'}</td>
                            <td style={{ padding: '5px 8px' }}>{r.rating || '—'}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600 }}>{fmtMoney(r.cost_cad)}</td>
                          </tr>
                        ))}
                        {(!data.topCost || data.topCost.length === 0) && (
                          <tr><td colSpan={7} style={{ padding: '1rem', textAlign: 'center', color: '#94a3b8' }}>No priced parts in this selection.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Panel>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Email report modal */}
      {showEmailModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10001 }}
          onClick={(e) => { e.stopPropagation(); if (!emailSending) setShowEmailModal(false); }}>
          <div style={{ backgroundColor: 'white', borderRadius: '10px', width: '90%', maxWidth: '460px', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#0f172a' }}>📧 Email Analytics Report</h3>
              <button onClick={() => { if (!emailSending) setShowEmailModal(false); }} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#64748b', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: '1.25rem' }}>
              <p style={{ margin: '0 0 0.6rem', fontSize: '0.82rem', color: '#475569' }}>
                Enter email addresses separated by commas. A PDF report (KPIs, MCAA vs OTTAV, size curves, top cost) will be attached.
              </p>
              <textarea
                value={emailAddresses}
                onChange={(e) => setEmailAddresses(e.target.value)}
                placeholder="name@laframboise.net, other@laframboise.net"
                rows={3}
                style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.82rem', resize: 'vertical' }}
              />
              {emailResult && (
                <div style={{ marginTop: '0.6rem', fontSize: '0.8rem', color: emailResult.type === 'error' ? '#dc2626' : '#16a34a' }}>
                  {emailResult.type === 'error' ? '⚠️ ' : '✓ '}{emailResult.message}
                </div>
              )}
              <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button onClick={() => setShowEmailModal(false)} disabled={emailSending}
                  style={{ padding: '7px 14px', backgroundColor: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem' }}>Close</button>
                <button onClick={handleSendEmail} disabled={emailSending || !emailAddresses.trim()}
                  style={{ padding: '7px 14px', backgroundColor: '#0ea5e9', color: 'white', border: 'none', borderRadius: '6px', cursor: emailSending || !emailAddresses.trim() ? 'not-allowed' : 'pointer', fontSize: '0.82rem', fontWeight: 600, opacity: emailSending || !emailAddresses.trim() ? 0.6 : 1 }}>
                  {emailSending ? 'Sending…' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartRateDbAnalytics;
